import _ from 'lodash';
import { createSelector } from 'reselect';
import { flattenOrderItems } from '../../../utils/PayPerDishUtils';
import { openrestHelpers } from '@wix/wixrest-utils';
import { getDisplayableDiscounts } from '../../../utils/DishUtils';
import { getPriceComponents } from '../../../utils/CartUtils';
import { applyOperatorsToManagerDiscounts } from '../../../utils/ChargeUtils';
import { deliveryTypeSelector } from './deliveryTypeSelector';

const { Item } = openrestHelpers;

const inputSelectors = [
  state => state.order.orderItems,
  state => state.session.menu,
  state => state.session.menu.chargesV2,
  state => state.session.organization,
  state => state.order.address,
  state => state.order.paymentAssociations,
  state => state.order.managerDiscount,
  state => state.order.manualDeliveryFee,
  state => deliveryTypeSelector(state),
];

function createSimpleOrder(orderItems) {
  return {
    orderItems,
    platform: 'callcenter',
    delivery: {
      type: 'takeout',
    },
  };
}

function unifyCharges(charges) {
  const aggregated = [];

  charges.forEach(charge => {
    const found = aggregated.find(c => c.chargeId === charge.chargeId);
    if (found) {
      found.amount += charge.amount;
    } else {
      aggregated.push(charge);
    }
  });

  return aggregated;
}

function getNumberOfPaymentMeans(paymentAssociations) {
  return paymentAssociations.filter(paymentAssociation => paymentAssociation.length > 0).length;
}

function derivedPpdDataFn(
  orderItems,
  menu,
  rawChargesV2,
  organization,
  address,
  paymentAssociations,
  managerDiscount,
  manualDeliveryFee,
  deliveryType,
) {
  const { timezone } = organization;
  const platform = 'callcenter';

  const deliveryTime = 'asap';
  const source = 'source';

  const chargesV2 = applyOperatorsToManagerDiscounts({
    chargesV2: rawChargesV2,
    amount: managerDiscount.amount,
    discountUnit: managerDiscount.unit,
  });

  const flatOrderItems = flattenOrderItems({ orderItems });
  return paymentAssociations.map(paymentAssociation => {
    const associatedOrderItems = paymentAssociation.map(dishIndex => flatOrderItems[dishIndex]);

    // create a fake order, just to match `Item.getItemCharges`'s API
    const simpleOrder = createSimpleOrder(orderItems);

    // separately calculate charges for each item associated with current payment against entire order
    const allItemsCharges = _.flatMap(associatedOrderItems, orderItem => {
      return Item.getItemCharges({
        orderItem,
        order: simpleOrder,
        chargesV2,
        timezone,
      });
    });

    const unifiedCharges = unifyCharges(allItemsCharges);

    const displayableDiscounts = getDisplayableDiscounts({
      menu,
      deliveryTime,
      deliveryType,
      orderItems: associatedOrderItems,
      source,
      platform,
      orderCharges: unifiedCharges,
    });

    const priceComponents = getPriceComponents({
      chargesV2,
      orderCharges: unifiedCharges,
      orderItems: associatedOrderItems,
      organization,
      address,
      numberOfPaymentMethods: getNumberOfPaymentMeans(paymentAssociations),
      paymentAssociation,
      manualDeliveryFee,
      ppd: true,
      dispatchType: deliveryType,
    });

    return {
      priceComponents,
      displayableDiscounts,
    };
  });
}

export const derivedPpdData = createSelector(
  inputSelectors,
  derivedPpdDataFn,
);
